import { Chip, FormControl, Grid, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TranslateRoundedIcon from "@mui/icons-material/TranslateRounded";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import AIQuizGeneratorWizard from "components/AIQuizGeneratorWizard";
import TranslateConfirmDialog from "components/TranslateConfirmDialog";
import { translateQuizTitleCall } from "../../redux/api/quizTranslation.api";
import Footer from "examples/Footer";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  AddQuizTitlesCall,
  DeleteQuizTitlesCall,
  EditQuizTitlesCall,
  GetQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import { setLang } from "../../redux/slices/generalSlice";
import { store } from "../../redux/store";
import { basicURL } from "../../apiManager/APIConfig";
import axios from "axios";
import { openAppConfirm } from "../../components/AppDialogs";

/*
  Quiz titles can now be authored against three categories:
    - histology  (pre-existing — was the only option)
    - embryology (new)
    - anatomy    (new)
  The backend already filters titles by ?type= on /api/quiz/title, so
  switching the dropdown just re-fetches with the chosen scope. New
  titles are created against the currently selected type.
*/
const QUIZ_TYPE_OPTIONS = [
  { value: "histology", label: "Histology" },
  { value: "embryology", label: "Embryology" },
  { value: "anatomy", label: "Anatomy" },
];

const QuizTitles = () => {
  const [items, setItems] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [message, setMessage] = useState("");
  const [uploadingId, setUploadingId] = useState(null);
  const [aiWizardOpen, setAiWizardOpen] = useState(false);
  /* When non-null, the per-title translate confirmation dialog is open
     for this specific title. We keep the full title object so the
     dialog can show its name + question count without re-fetching. */
  const [translateTarget, setTranslateTarget] = useState(null);
  const lang = useSelector((state) => state.general?.lang);
  /* Type is now a piece of state — the dropdown drives both the
     fetch scope and what category newly-added titles belong to. */
  const [type, setType] = useState("histology");
  const theme = useTheme();
  const imageInputRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cardBg = theme.palette.background?.card ?? theme.palette.background?.paper ?? theme.palette.background?.default;
  const textPrimary = theme.palette.text.primary;
  const isDark = theme.palette.mode === "dark";
  const inputBg = isDark ? (theme.palette.grey?.[800] ?? "#2d2d2d") : theme.palette.background.paper;
  const inputBorder = theme.palette.divider;
  const inputTextColor = theme.palette.text.primary;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: inputBg,
      color: inputTextColor,
      "& fieldset": { borderColor: inputBorder },
      "&:hover fieldset": { borderColor: "primary.main" },
      "&.Mui-focused fieldset": { borderColor: "primary.main" },
      "& input": { color: inputTextColor },
    },
    "& .MuiInputLabel-root": { color: theme.palette.text.secondary },
    "& .MuiInputLabel-root.Mui-focused": { color: "primary.main" },
  };

  const fetchTitles = async () => {
    const res = await GetQuizTitlesCall({ lang, type });
    if (res?.status === 200) {
      setItems(res.data);
    }
  };

  const handleLanguageChange = (event) => {
    // setLanguage(event.target.value);
    dispatch(setLang(event.target.value));
    fetchTitles();
  };

  /* Switching the category dropdown re-scopes the list and ensures
     subsequent "Add title" calls land in the chosen bucket. */
  const handleTypeChange = (event) => {
    setType(event.target.value);
  };

  const handleAddQuestion = (id) => {
    navigate(`/showQuestions/${id}/${lang}`);
  };

  // Re-fetch whenever the user switches category or language so the
  // list always reflects the active scope. fetchTitles is a stable
  // closure over those two values, so we intentionally only depend
  // on `type` and `lang` here.
  useEffect(() => {
    fetchTitles();
  }, [type, lang]);

  const handleAddTitle = async () => {
    const trimmed = (newTitle || "").trim();
    if (!trimmed) {
      setMessage("Please enter a title");
      return;
    }
    setMessage("");
    const res = await AddQuizTitlesCall({
      title: trimmed,
      lang,
      type,
    });
    if (res?.status === 201) {
      setMessage("Title added successfully!");
      setNewTitle("");
      fetchTitles();
    } else {
      setMessage(res?.message || "Failed to add title");
    }
  };

  const handleEdit = async (id, newValue) => {
    const res = await EditQuizTitlesCall({ title_id: id, title: newValue, lang, type });
    if (res?.status === 200) fetchTitles();
  };
  const handleTriggerImageUpload = (titleId) => {
    imageInputRef.current?.setAttribute?.("data-title-id", titleId);
    imageInputRef.current?.click?.();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    const titleId = imageInputRef.current?.getAttribute?.("data-title-id");
    if (!file || !titleId) return;
    e.target.value = "";
    try {
      setUploadingId(titleId);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("entity_type", "quiz_title");
      formData.append("entity_id", titleId);
      const token = store.getState().auth?.access_token;
      const res = await axios.post(`${basicURL}file/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const downloadUrl = res?.data?.download_url;
      if (downloadUrl) {
        const editRes = await EditQuizTitlesCall({ title_id: titleId, image: downloadUrl, lang, type });
        if (editRes?.status === 200) fetchTitles();
      }
    } catch (err) {
      console.error("Image upload failed:", err);
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteTitle = async (id) => {
    const ok = await openAppConfirm({
      title: "Delete title?",
      message: "Are you sure you want to delete this title?",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (!ok) return;
    const res = await DeleteQuizTitlesCall({ title_id: id });
    if (res?.status === 200) {
      setMessage("Deleted successfully!");
      fetchTitles();
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={0} pt={3} pb={3}>
        <MDBox py={1} display="flex" justifyContent="flex-end" mb={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <TextField
                size="small"
                label="New title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddTitle()}
                placeholder="Enter title name"
                sx={{ minWidth: 220, ...inputSx }}
              />
          </Grid>
          <Grid item>
            <MDButton color="info" variant="contained" onClick={() => handleAddTitle()}>
              + Add Title
            </MDButton>
          </Grid>
          <Grid item>
            <MDButton
              color="primary"
              variant="gradient"
              onClick={() => setAiWizardOpen(true)}
              startIcon={<AutoAwesomeRoundedIcon />}
            >
              Generate with AI
            </MDButton>
          </Grid>
          {message && (
            <Grid item xs={12} sm="auto">
              <span style={{ color: message.includes("success") ? "green" : "inherit" }}>{message}</span>
            </Grid>
          )}
          {/* Category + Language dropdowns on the right */}
          <Grid
            item
            xs
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
              gap: 12,
            }}
          >
            <FormControl sx={{ width: 200, "& .MuiInputBase-root": { minHeight: 48 } }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={type}
                label="Category"
                onChange={handleTypeChange}
                sx={{ minHeight: 48 }}
              >
                {QUIZ_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: 220, "& .MuiInputBase-root": { minHeight: 48 } }}>
              <InputLabel>Language</InputLabel>
              <Select value={lang} onChange={handleLanguageChange} sx={{ minHeight: 48 }}>
                <MenuItem value="en">English</MenuItem>
                <MenuItem value="pt">Portugal</MenuItem>
                <MenuItem value="es">Spanish</MenuItem>
                <MenuItem value="it">Italian</MenuItem>
                <MenuItem value="ge">Germany</MenuItem>
                <MenuItem value="nl">Dutch</MenuItem>
                <MenuItem value="cn">Chines</MenuItem>
                <MenuItem value="ja">Japanese</MenuItem>
                <MenuItem value="ar">Arabic</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox py={2} px={3}>
        <input
          type="file"
          ref={imageInputRef}
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />
        <Grid container spacing={3}>
          {items.map((t, index) => (
            <MDBox
              key={index}
              p={2}
              display="flex"
              flexDirection="column"
              alignItems="center"
              borderRadius="8px"
                style={{ marginBottom: 15, backgroundColor: cardBg, marginRight:10 }}
            >
              {t?.image ? (
                <img
                  src={t.image}
                  alt=""
                  style={{
                    width: 100,
                    height: "auto",
                    borderRadius: "8px",
                    marginBottom: "10px",
                    objectFit: "cover",
                    minHeight: 60,
                  }}
                />
              ) : (
                <MDBox
                  sx={{
                    width: 100,
                    minHeight: 60,
                    borderRadius: "8px",
                    marginBottom: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "action.hover",
                    color: "text.secondary",
                  }}
                >
                  <AddPhotoAlternateIcon sx={{ fontSize: 80 }} />
                </MDBox>
              )}
              <li style={{ display: "flex", flexDirection: "column", listStyle: "none", width: "100%" }} key={t.id}>
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    marginTop: 10,
                    marginBottom: 5,
                    fontWeight: "bold",
                    color: textPrimary,
                  }}
                >
                  Title
                </label>
                <TextField
                  size="small"
                  defaultValue={t.title}
                  onBlur={(e) => handleEdit(t.id, e.target.value)}
                  fullWidth
                  sx={{ mt: 0.5, ...inputSx }}
                />
                <label
                  style={{
                    display: "block",
                    fontSize: 14,
                    marginTop: 20,
                    marginBottom: 5,
                    fontWeight: "bold",
                    color: textPrimary,
                  }}
                >
                  Image
                </label>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  disabled={uploadingId === t.id}
                  onClick={() => handleTriggerImageUpload(t.id)}
                  sx={{ mt: 0.5 }}
                >
                  {uploadingId === t.id ? "Uploading…" : "Upload image"}
                </MDButton>
              </li>
              <div style={{ gap: 10, display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
                <button
                  style={{
                    backgroundColor: "red",
                    color: "white",
                    borderWidth: 0,
                    borderRadius: 6,
                    marginTop: 20,
                    padding: 8,
                  }}
                  onClick={() => handleDeleteTitle(t.id)}
                >
                  Delete
                </button>
                <button
                  style={{
                    backgroundColor: "green",
                    color: "white",
                    borderWidth: 0,
                    borderRadius: 6,
                    marginTop: 20,
                    padding: 8,
                  }}
                  onClick={() => handleAddQuestion(t.id)}
                >
                  Add Questions
                </button>
                {/* Translate button — opens a confirm dialog that
                    cascades over the title text + every question. */}
                <button
                  title="Translate this title and all its questions to every supported language"
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    color: "white",
                    borderWidth: 0,
                    borderRadius: 6,
                    marginTop: 20,
                    padding: 8,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer",
                  }}
                  onClick={() => setTranslateTarget(t)}
                >
                  <TranslateRoundedIcon style={{ fontSize: 16 }} />
                  Translate
                </button>
              </div>
            </MDBox>
          ))}
        </Grid>
      </MDBox>
      </MDBox>
      <AIQuizGeneratorWizard
        open={aiWizardOpen}
        onClose={() => setAiWizardOpen(false)}
        mode="general"
        lang={lang}
        defaultCategory={type}
        existingTitles={items.map((t) => ({ id: t.id, title: t.title }))}
        onSaved={({ savedCount, failedCount, lang: savedLang, detectedLanguage }) => {
          const langLabel =
            detectedLanguage?.label || (savedLang ? savedLang.toUpperCase() : "");
          const langSuffix = langLabel ? ` (${langLabel})` : "";
          const localeNote =
            savedLang && savedLang !== lang
              ? ` Switch the Language picker to ${langLabel} to see them.`
              : "";
          setMessage(
            failedCount
              ? `Saved ${savedCount} questions${langSuffix} (${failedCount} failed).${localeNote}`
              : `Saved ${savedCount} question${savedCount === 1 ? "" : "s"}${langSuffix} successfully!${localeNote}`,
          );
          fetchTitles();
        }}
      />
      <TranslateConfirmDialog
        open={!!translateTarget}
        onClose={() => setTranslateTarget(null)}
        title="Translate title and all its questions"
        description={
          translateTarget ? (
            <>
              The title <strong>{translateTarget.title || "(untitled)"}</strong>{" "}
              and every question it contains will be translated into all
              supported languages using AI.
            </>
          ) : null
        }
        targetLangsLabel="English, Portuguese, Spanish, Italian, German, Dutch, Chinese, Japanese, Arabic (source locale is skipped)"
        itemSummaryLabel={
          translateTarget
            ? `Title + all its questions`
            : ""
        }
        confirmLabel="Translate to all languages"
        onConfirm={async ({ setStatus }) => {
          setStatus("Translating title and questions…");
          const res = await translateQuizTitleCall(translateTarget?.id);
          return res;
        }}
        summaryRenderer={(res) => (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Title translated into {res?.titleTargetLangs?.length ?? 0} language
              {res?.titleTargetLangs?.length === 1 ? "" : "s"} ·{" "}
              {res?.questionsTranslated ?? 0} of {res?.questionCount ?? 0}{" "}
              question{(res?.questionCount ?? 0) === 1 ? "" : "s"} translated
              {res?.questionsFailed ? ` · ${res.questionsFailed} failed` : ""}.
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {(res?.titleTargetLangs || []).map((code) => (
                <Chip
                  key={code}
                  size="small"
                  color="success"
                  variant="outlined"
                  label={code.toUpperCase()}
                  sx={{ mb: 0.5 }}
                />
              ))}
            </Stack>
          </Stack>
        )}
      />
      <Footer />
    </DashboardLayout>
  );
};

export default QuizTitles;
